<template>
  <div class="register-container" @keydown.enter="register">
    <div class="washi-title">{{ $t('register.title') }}</div>
    <div class="account-infos">
      <b>{{ $t('register.createAccount') }}</b>
      {{ $t('register.andStartToUse') }}
      <b>{{ $t('projectName') }}.</b>

      <div class="already-have-account">
        {{ $t('register.alreadyHaveAccount') }}
        <router-link :to="{ name: 'login' }" class="link2">{{ $t('register.login') }}</router-link>
      </div>
    </div>
    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.email') }}</div>
      <input
        ref="email"
        v-model="email"
        type="text"
        :data-value="!!email"
        class="washi-input"
        @change="checkEmail"
        @blur="checkEmail"
      />
    </div>
    <div v-if="errorCode === emailErrorCode" class="error">
      {{ $t('register.errors.emailExist') }}
    </div>
    <div v-if="errorCode === emailInvalidErrorCode" class="error">
      {{ $t('register.errors.emailIsInvalid') }}
    </div>
    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.password') }}</div>
      <input
        ref="password"
        v-model="password"
        type="password"
        :data-value="!!password"
        class="washi-input"
        @keyup="checkSecondPassword"
      />
    </div>

    <div
      class="password-checker"
      :class="{
        error: !(minimumLetters || hasUppercase || specialCharacter || oneNumber) && isSend,
      }"
    >
      <div
        class="title"
        :class="{
          red: !(minimumLetters || hasUppercase || specialCharacter || oneNumber) && isSend,
        }"
      >
        {{ $t('register.password.shouldContain') }}
      </div>
      <div class="line" :class="{ error: isSend && !minimumLetters, checked: minimumLetters }">
        <div class="checkable"><check class="icon" /></div>
        <div class="value">{{ $t('register.password.minimumLetters') }}</div>
      </div>
      <div class="line" :class="{ error: isSend && !hasUppercase, checked: hasUppercase }">
        <div class="checkable"><check class="icon" /></div>
        <div class="value">{{ $t('register.password.capital') }}</div>
      </div>
      <div
        class="line"
        :class="{
          error: isSend && !(specialCharacter || oneNumber),
          checked: specialCharacter || oneNumber,
        }"
      >
        <div class="checkable"><check class="icon" /></div>
        <div class="value">{{ $t('register.password.numberOrSymbol') }}</div>
      </div>
    </div>

    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.passwordConfirm') }}</div>
      <input
        ref="password2"
        v-model="password2"
        type="password"
        :data-value="!!password2"
        class="washi-input"
        @keyup="checkSecondPassword"
      />
    </div>
    <div v-if="errorCode === ConfirmErrorCode" class="error">
      {{ $t('register.errors.confirmError') }}
    </div>
    <div class="spacer" />
    <div class="sign-in main-button" @click="register">
      <span class="text">{{ $t('login.buttons.signup') }}</span>
    </div>
    <div class="spacer">
      <div class="divider" />
      <div class="text">ou</div>
      <div class="divider" />
    </div>
    <div class="main-button sign-in-google" @click="googleSignIn">
      <googleLogo class="google-logo" />
      <div class="divider" />
      <span class="text">{{ $t('login.buttons.google') }}</span>
    </div>
  </div>
</template>

<script>
import check from '@/assets/icons/checked.svg';
import googleLogo from '@/assets/icons/google.svg';
import { requestCode } from 'vue-google-identity';

const emailErrorCode = 400902;
const emailInvalidErrorCode = 400901;
const ConfirmErrorCode = 600001;
export default {
  name: 'RegisterBody',
  components: {
    check,
    googleLogo,
  },
  props: {
    checkAction: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      ConfirmErrorCode,
      loading: false,
      isSend: false,
      email: '',
      lastname: '',
      firstname: '',
      password: '',
      password2: '',
      errorCode: null,
      emailErrorCode,
      emailInvalidErrorCode,
      checkerErrors: [],
    };
  },
  computed: {
    minimumLetters() {
      return this.password.length >= 8;
    },
    specialCharacter() {
      return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(this.password);
    },
    oneNumber() {
      return /[0123456789]+/.test(this.password);
    },
    hasUppercase() {
      return this.password.toLowerCase() !== this.password && this.password.toUpperCase() !== this.password;
    },
    checkMail() {
      if (this.email === '') return true;
      const regMail = /^(\.|\+|[\w-])*@([a-z0-9]+(\.?[a-z0-9-]+)*?\.[a-z]{2,99}|(\d{1,5}\.){3}\d{1,3})(:\d{4})?$/;
      return regMail.test(this.email);
    },
  },
  created() {
    const action = this.$route.query.action;
    const token = this.$route.query.token;
    if (action && token) {
      localStorage.setItem('action', action);
      localStorage.setItem('token', token);
    }
  },
  methods: {
    async googleSignIn() {
      if (this.loading) return;
      this.loading = true;
      try {
        const response = await new Promise(async (resolve, reject) => {
          const res = await requestCode();
          if (res.error) {
            reject(new Error(res.error));
          } else {
            resolve(res);
          }
        });
        await this.$store.dispatch('auth.loginWithGoogle', {
          offlineCode: response.code,
        });

        // fetch async user info to populate store
        await this.checkAction();
        this.$store.dispatch('templates.fetch');
        this.$router.push({ name: 'dashboard' });
      } catch (e) {
        console.error(e);
        alert(e);
      }
      this.loading = false;
    },
    async checkSecondPassword() {
      if (this.loading || !this.isSend || !this.$refs.password2) return;
      if (this.checkValidation()) {
        this.$refs.password.setCustomValidity('');
        if (this.password === this.password2) {
          this.$refs.password2.setCustomValidity('');
          this.errorCode = null;
        }
      }
    },
    async register() {
      const lastResumeId = localStorage.getItem('editingResume');

      if (this.loading) return;
      this.$refs.password.setCustomValidity('');
      this.$refs.password2.setCustomValidity('');
      this.$refs.email.setCustomValidity('');
      this.loading = true;
      this.isSend = true;
      this.errorCode = '';
      if (!this.email || !this.password || !this.password2) {
        if (!this.email) this.$refs.email.setCustomValidity('Invalid');
        if (!this.password) this.$refs.password.setCustomValidity('Invalid');
        if (!this.password2) this.$refs.password2.setCustomValidity('Invalid');
        this.loading = false;
        return;
      }
      if (!this.checkValidation()) {
        this.$refs.password.setCustomValidity('Invalid');
        this.$refs.password2.setCustomValidity('Invalid');
        if (!this.checkMail) {
          this.$refs.email.setCustomValidity('Invalid');
          this.errorCode = this.emailInvalidErrorCode;
        }
        this.loading = false;
        return;
      } else {
        if (!this.checkMail) {
          this.$refs.email.setCustomValidity('Invalid');
          this.errorCode = this.emailInvalidErrorCode;
          this.loading = false;
          return;
        }
      }

      try {
        if (this.password !== this.password2) {
          this.$refs.password2.setCustomValidity('Invalid');
          this.errorCode = this.ConfirmErrorCode;
          this.loading = false;
          return;
        }
        localStorage.removeItem('editingResume');

        await this.$store.dispatch('auth.register', {
          email: this.email,
          password: this.password,
          firstname: this.firstname,
          lastname: this.lastname,
          resumeId: lastResumeId,
        });
        this.$router.push({ name: 'validateEmail' });
      } catch (e) {
        if (e.response) {
          switch (e.response.data.code) {
            case emailErrorCode:
              this.$refs.email.setCustomValidity('Invalid');
              break;
            case emailInvalidErrorCode:
              this.$refs.email.setCustomValidity('Invalid');
              break;
          }
          this.errorCode = e.response.data.code;
        } else {
          this.errorCode = true;
        }
        console.error(e);
      }
      this.loading = false;
    },
    checkEmail() {
      if (!this.isSend) this.$refs.email.setCustomValidity('');
      else if (this.checkMail) {
        this.errorCode = '';
        this.$refs.email.setCustomValidity('');
      } else {
        this.errorCode = this.emailInvalidErrorCode;
        this.$refs.email.setCustomValidity('Invalid');
      }
    },
    checkValidation() {
      const checkerErrors = [];
      if (!this.minimumLetters) checkerErrors.push(0);
      if (!this.oneNumber && !this.specialCharacter) checkerErrors.push(2);
      if (!this.hasUppercase) checkerErrors.push(3);
      return this.password && checkerErrors.length === 0;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.error {
  color: colors.$red-color;
  font-size: 16px;
  margin: 12px 0;
  align-self: flex-end;
}
.input-container {
  margin: 12px 0;
  width: 100%;
  .input-title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 4px;
  }
}
.register-container {
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 128px;
  box-sizing: border-box;

  .washi-title {
    align-self: flex-start;
    font-size: 36px;
    font-weight: bold;
  }

  .account-infos {
    font-weight: lighter;
    align-self: flex-start;
    margin: 12px 0;
    font-size: 20px;
    line-height: 30px;

    .link2 {
      color: colors.$secondary-color;
      font-weight: bold;

      &:hover {
        color: colors.$dark-yellow-color;
      }
    }

    b {
      font-weight: normal;
    }

    .already-have-account {
      display: flex;
      gap: 8px;
    }
  }

  .password-checker {
    align-self: flex-start;
    margin: 12px 0;
    padding-left: 16px;
    border-left: 1px solid colors.$light-blue-color;

    .title {
      color: colors.$light-blue-color;

      &.red {
        color: colors.$red-color;
      }
    }

    &.error .line {
      &:not(.checked) {
        color: colors.$red-color;
      }
    }

    &:not(.error) .line .value {
      font-weight: lighter;
    }

    .line {
      display: flex;
      margin: 12px 0;

      .checkable {
        width: 22px;
        height: 22px;
        margin-right: 8px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: colors.$lighter-yellow-color;
        transition: all 0.3s;

        .icon {
          display: none;
          transition: all 0.3s;
        }
      }
      &.checked {
        .checkable {
          background: colors.$light-blue-color;
        }

        .icon {
          display: block;
          color: #fff !important;
          width: 8px;
          height: 8px;
        }
      }

      .value {
        transition: all 0.3s;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: normal;
      }

      &.error {
        color: colors.$red-color;
      }
    }
  }

  .forgot-password {
    align-self: flex-end;
    margin-right: 8px;
  }
  .main-button {
    width: 184px;
    height: 42px;
    font-size: 14px;
    box-sizing: border-box;
    &.sign-in-google {
      display: flex;
      justify-content: space-around;
      padding: 0 12px 0 0;
      text-align: left;
      .google-logo {
        width: 22px;
        height: 100%;
        margin: 16px 0 16px 16px;
        flex-shrink: 0;
      }
      .divider {
        width: 1px;
        height: 100%;
        background-color: currentColor;
        flex-shrink: 0;
        margin: 0 8px;
      }
    }
  }
  .spacer {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin: 16px 0;
    min-height: 12px;
    .text {
      margin: 0 64px;
      font-size: 20px;
    }
    .divider {
      height: 1px;
      flex-grow: 1;
      background-color: colors.$secondary-color;
    }
  }
  .no-account {
    font-size: 16px;
  }
}

@media screen and (max-width: 1184px) {
  .input-container .input-title {
    font-size: 14px;
  }

  .line {
    margin: 8px 0;
  }

  .register-container {
    width: 100% !important;
    padding: 0 26px !important;
    margin-bottom: 24px;
    min-width: 260px;

    .washi-title {
      font-size: 21px;
      margin-bottom: 20px;
    }

    .spacer .text,
    .account-infos {
      font-size: 16px;
    }
  }
}
</style>
